import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { Button, Checkbox, EmptyState, Input, Pill, Select, Textarea, useToast } from "@astrum/ui";
import type { CustomField, FormQuestion, IntakeForm } from "@astrum/shared";
import { api, ApiError } from "../lib/api.js";
import { CORE_TARGET_LABELS, QUESTION_TYPE_LABELS, formUrl, newQuestionId, targetFromKey, targetKey, typesForTarget } from "../lib/intake.js";
import "./project-forms-tab.css";

type Member = { id: string; displayName: string };

interface Props {
  projectId: string;
  members: Member[];
  /** Project managers and editors build forms; everyone else only sees the enabled ones. */
  canEdit: boolean;
}

type Draft = {
  id: string | null;
  title: string;
  description: string;
  enabled: boolean;
  questions: FormQuestion[];
  defaultAssigneeId: string;
  requireApproval: boolean;
  approverIds: string[];
};

const blankDraft = (): Draft => ({
  id: null,
  title: "",
  description: "",
  enabled: true,
  questions: [{ id: newQuestionId(), label: "What do you need?", type: "short_text", required: true, target: { kind: "core", field: "title" } }],
  defaultAssigneeId: "",
  requireApproval: false,
  approverIds: [],
});

const draftOf = (f: IntakeForm): Draft => ({
  id: f.id,
  title: f.title,
  description: f.description ?? "",
  enabled: f.enabled,
  questions: f.questions,
  defaultAssigneeId: f.defaultAssigneeId ?? "",
  requireApproval: f.requireApproval,
  approverIds: f.approverIds,
});

/**
 * Project settings for intake forms: build a form from questions that fill in
 * a task's title, description, due date, assignee or a custom field, switch it
 * on, and share its link. Anyone in the office may fill in an enabled form.
 */
export function ProjectFormsTab({ projectId, members, canEdit }: Props) {
  const { toast } = useToast();
  const [forms, setForms] = useState<IntakeForm[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [fields, setFields] = useState<CustomField[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);

  const fail = useCallback((error: unknown, fallback: string) => toast(error instanceof ApiError ? error.message : fallback, { tone: "danger" }), [toast]);

  const load = useCallback(
    async (before?: string) => {
      try {
        const res = await api.get<{ forms: IntakeForm[]; nextCursor: string | null }>(
          `/projects/${projectId}/forms${before ? `?before=${encodeURIComponent(before)}` : ""}`,
        );
        setForms((prev) => (before ? [...(prev ?? []), ...res.forms] : res.forms));
        setNextCursor(res.nextCursor);
      } catch (e) {
        setForms((prev) => prev ?? []);
        fail(e, "Could not load the forms.");
      }
    },
    [projectId, fail],
  );

  useEffect(() => {
    void load();
    api
      .get<{ fields: CustomField[] }>(`/projects/${projectId}/custom-fields`)
      .then((res) => setFields(res.fields))
      .catch(() => setFields([]));
  }, [projectId, load]);

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    const body = {
      title: draft.title,
      description: draft.description.trim() === "" ? null : draft.description,
      enabled: draft.enabled,
      questions: draft.questions.map((q) => ({ ...q, help: q.help?.trim() || undefined })),
      defaultAssigneeId: draft.defaultAssigneeId || null,
      requireApproval: draft.requireApproval,
      approverIds: draft.requireApproval ? draft.approverIds : [],
    };
    try {
      if (draft.id) {
        const res = await api.patch<{ form: IntakeForm }>(`/forms/${draft.id}`, body);
        setForms((prev) => (prev ?? []).map((f) => (f.id === res.form.id ? res.form : f)));
      } else {
        const res = await api.post<{ form: IntakeForm }>(`/projects/${projectId}/forms`, body);
        setForms((prev) => [res.form, ...(prev ?? [])]);
      }
      setDraft(null);
    } catch (e) {
      fail(e, "Could not save the form.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (form: IntakeForm) => {
    if (!window.confirm(`Delete "${form.title}"? The record of who filed requests through it is deleted too; the tasks stay.`)) return;
    try {
      await api.delete(`/forms/${form.id}`);
      setForms((prev) => (prev ?? []).filter((f) => f.id !== form.id));
    } catch (e) {
      fail(e, "Could not delete the form.");
    }
  };

  const copyLink = async (form: IntakeForm) => {
    try {
      await navigator.clipboard.writeText(formUrl(form.id));
      toast("Link copied.");
    } catch {
      toast(formUrl(form.id));
    }
  };

  const setQuestion = (index: number, patch: Partial<FormQuestion>) =>
    setDraft((d) => d && { ...d, questions: d.questions.map((q, i) => (i === index ? { ...q, ...patch } : q)) });

  const retarget = (index: number, key: string) => {
    const target = targetFromKey(key);
    const types = typesForTarget(target, fields);
    const current = draft?.questions[index];
    setQuestion(index, { target, type: current && types.includes(current.type) ? current.type : (types[0] ?? "short_text") });
  };

  const usedTargets = new Set(draft?.questions.map((q) => targetKey(q.target)));

  if (forms === null) return null;

  return (
    <div className="project-forms">
      {!draft && (
        <>
          {canEdit && (
            <div>
              <Button size="sm" onClick={() => setDraft(blankDraft())}>
                New form
              </Button>
            </div>
          )}
          {forms.length === 0 ? (
            <EmptyState icon="document" size="inline" title="No forms yet" description="A form lets anyone in the office send this project a request that becomes a task." />
          ) : (
            <ul className="project-forms__list">
              {forms.map((form) => (
                <li key={form.id} className="project-forms__item">
                  <div className="project-forms__main">
                    <Link to={`/forms/${form.id}`} className="project-forms__title">
                      {form.title}
                    </Link>
                    <span className="project-forms__meta">
                      {form.questions.length} question{form.questions.length === 1 ? "" : "s"}
                    </span>
                    {!form.enabled && <Pill tone="neutral">Off</Pill>}
                    {form.requireApproval && <Pill tone="warn">Needs approval</Pill>}
                  </div>
                  <div className="project-forms__actions">
                    <Button size="sm" variant="ghost" onClick={() => void copyLink(form)}>
                      Copy link
                    </Button>
                    {canEdit && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setDraft(draftOf(form))}>
                          Edit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => void remove(form)}>
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {nextCursor && (
            <div>
              <Button size="sm" variant="secondary" onClick={() => void load(nextCursor)}>
                Show more
              </Button>
            </div>
          )}
        </>
      )}

      {draft && (
        <div className="project-forms__editor">
          <Input label="Form title" value={draft.title} maxLength={120} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <Textarea label="Description" rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />

          <h3 className="project-forms__heading">Questions</h3>
          {draft.questions.map((q, i) => {
            const types = typesForTarget(q.target, fields);
            return (
              <div key={q.id} className="project-forms__question">
                <Input label="Question" value={q.label} maxLength={120} onChange={(e) => setQuestion(i, { label: e.target.value })} />
                <Select label="Fills in" value={targetKey(q.target)} onChange={(e) => retarget(i, e.target.value)}>
                  {(Object.keys(CORE_TARGET_LABELS) as (keyof typeof CORE_TARGET_LABELS)[]).map((field) => {
                    const key = `core:${field}`;
                    return (
                      <option key={key} value={key} disabled={usedTargets.has(key) && key !== targetKey(q.target)}>
                        {CORE_TARGET_LABELS[field]}
                      </option>
                    );
                  })}
                  {fields.map((f) => {
                    const key = `custom:${f.id}`;
                    return (
                      <option key={key} value={key} disabled={usedTargets.has(key) && key !== targetKey(q.target)}>
                        {f.name} (custom field)
                      </option>
                    );
                  })}
                </Select>
                {types.length > 1 && (
                  <Select label="Answer" value={q.type} onChange={(e) => setQuestion(i, { type: e.target.value as FormQuestion["type"] })}>
                    {types.map((t) => (
                      <option key={t} value={t}>
                        {QUESTION_TYPE_LABELS[t]}
                      </option>
                    ))}
                  </Select>
                )}
                <Checkbox
                  label="Required"
                  checked={q.required || (q.target.kind === "core" && q.target.field === "title")}
                  disabled={q.target.kind === "core" && q.target.field === "title"}
                  onChange={(e) => setQuestion(i, { required: e.target.checked })}
                />
                {!(q.target.kind === "core" && q.target.field === "title") && (
                  <Button size="sm" variant="ghost" onClick={() => setDraft({ ...draft, questions: draft.questions.filter((_, j) => j !== i) })}>
                    Remove question
                  </Button>
                )}
              </div>
            );
          })}
          <div>
            <Button
              size="sm"
              variant="secondary"
              disabled={draft.questions.length >= 30}
              onClick={() => {
                const free =
                  (["description", "dueAt", "assignee"] as const).map((f) => ({ kind: "core" as const, field: f })).find((t) => !usedTargets.has(targetKey(t))) ??
                  fields.map((f) => ({ kind: "custom" as const, fieldId: f.id })).find((t) => !usedTargets.has(targetKey(t)));
                if (!free) return toast("Every field already has a question.");
                const types = typesForTarget(free, fields);
                setDraft({
                  ...draft,
                  questions: [...draft.questions, { id: newQuestionId(), label: "", type: types[0] ?? "short_text", required: false, target: free }],
                });
              }}
            >
              Add question
            </Button>
          </div>

          <h3 className="project-forms__heading">When someone submits</h3>
          <Select label="Assign to" value={draft.defaultAssigneeId} onChange={(e) => setDraft({ ...draft, defaultAssigneeId: e.target.value })}>
            <option value="">Nobody (the project owner is told)</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.displayName}
              </option>
            ))}
          </Select>
          <Checkbox
            label="Every request needs approval"
            checked={draft.requireApproval}
            onChange={(e) => setDraft({ ...draft, requireApproval: e.target.checked })}
          />
          {draft.requireApproval && (
            <div className="project-forms__approvers">
              {members.map((m) => (
                <Checkbox
                  key={m.id}
                  label={m.displayName}
                  checked={draft.approverIds.includes(m.id)}
                  onChange={(e) =>
                    setDraft({ ...draft, approverIds: e.target.checked ? [...draft.approverIds, m.id] : draft.approverIds.filter((id) => id !== m.id) })
                  }
                />
              ))}
            </div>
          )}
          <Checkbox label="Taking requests" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />

          <div className="project-forms__actions">
            <Button size="sm" disabled={busy || draft.title.trim() === ""} onClick={() => void save()}>
              {draft.id ? "Save form" : "Create form"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
